import { useEffect, useState } from 'react';
import { api, type Me } from '../../lib/api';

const ROLES = ['learner', 'instructor', 'admin'] as const;
const STATUSES = ['active', 'suspended'] as const;

interface Props { me?: Me; }

export default function Users(_props: Props) {
  const [users, setUsers] = useState<any[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  async function load() {
    const r = await api.adminUsers();
    setUsers(r.users);
  }
  useEffect(() => { load(); }, []);

  async function patch(id: string, body: any) {
    setBusy(id);
    try { await api.adminUpdateUser(id, body); await load(); }
    catch (e: any) { alert(`Failed: ${e.message || e}`); }
    finally { setBusy(null); }
  }

  async function revokeSessions(id: string, name: string) {
    if (!confirm(`Force ${name} to sign in again? All active sessions will be revoked.`)) return;
    setBusy(id);
    try { const r = await api.adminRevokeUserSessions(id); alert(`Revoked ${r.revoked} session(s).`); }
    catch (e: any) { alert(`Failed: ${e.message || e}`); }
    finally { setBusy(null); }
  }

  const filtered = (users || []).filter((u) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return [u.email, u.display_name, u.role, u.pro_role].some((v) => v && String(v).toLowerCase().includes(f));
  });

  return (
    <div>
      <div className="flex justify-between items-end mb-4 flex-wrap gap-2">
        <h1 className="text-2xl font-semibold">Users</h1>
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="filter by email / name / role"
          className="bg-ink-800 border border-ink-800 rounded px-3 py-2 text-mono-white text-sm focus:border-brand-600 focus:outline-none w-72"
        />
      </div>
      {users == null && <p className="text-mono-white/60">Loading…</p>}
      {users && (
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-mono-white/40 bg-ink-800/50">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Email · display name</th>
                <th className="text-left px-3 py-2 font-medium">Role</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Region</th>
                <th className="text-left px-3 py-2 font-medium">Runs</th>
                <th className="text-left px-3 py-2 font-medium">Avg</th>
                <th className="text-left px-3 py-2 font-medium">Last run</th>
                <th className="text-right px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-t border-ink-800 hover:bg-ink-800/30 align-top">
                  <td className="px-3 py-2">
                    <div className="text-mono-white">{u.email || <span className="text-mono-white/40 italic">no email on file</span>}</div>
                    <div className="text-[10px] text-mono-white/40 font-mono">{u.display_name} · {u.id}</div>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={u.role}
                      onChange={(e) => patch(u.id, { role: e.target.value })}
                      disabled={busy === u.id || u.role === 'superadmin'}
                      className="bg-ink-800 border border-ink-800 rounded px-2 py-1 text-xs uppercase tracking-widest text-mono-white focus:border-brand-600 focus:outline-none disabled:opacity-50"
                    >
                      {u.role === 'superadmin' && <option value="superadmin">superadmin</option>}
                      {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={u.status}
                      onChange={(e) => patch(u.id, { status: e.target.value })}
                      disabled={busy === u.id}
                      className={`bg-ink-800 border border-ink-800 rounded px-2 py-1 text-xs uppercase tracking-widest focus:border-brand-600 focus:outline-none ${u.status === 'suspended' ? 'text-alarm-red' : 'text-mono-green'}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{u.region_default}</td>
                  <td className="px-3 py-2 font-mono">{u.run_count ?? 0}</td>
                  <td className="px-3 py-2 font-mono">{u.avg_score != null ? Number(u.avg_score).toFixed(0) : '—'}</td>
                  <td className="px-3 py-2 text-xs text-mono-white/60">{u.last_run_at ? new Date(u.last_run_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => revokeSessions(u.id, u.email || u.display_name)}
                      disabled={busy === u.id}
                      className="btn-ghost text-[10px] uppercase tracking-widest py-1 px-2"
                      title="Revoke all active sessions — forces re-login on next visit"
                    >
                      Revoke sessions
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[10px] text-mono-white/40 mt-3">
        Email column is admin-only. Backfilled from <code className="font-mono text-mono-cyan">magic_link_pending</code> on 2026-05-19; new sign-ins populate automatically.
      </p>
    </div>
  );
}
